import { WorkingType } from '../models/Schedule';
import DateUtils from './DateUtils';
export class Calendar {
    /**
     * It is possible for a project calendar to be configured with no working
     * days. This will result in an infinite loop when looking for the next
     * working day from a date, so we use this constant to set a limit on the
     * maximum number of non-working days we'll skip before we bail out
     * and take an alternative approach.
     */
    static MAX_NONWORKING_DAYS = 1000;
    /** Built-in 24-hour calendar used by CPM; not persisted to the project calendars DB. */
    static TWENTY_FOUR_HOUR_UNIQUE_ID = -1;
    static getDefaultCalendar() {
        return new Calendar(this.getDefaultCalendarDoc());
    }
    static getDefaultCalendarDoc() {
        const workingDay = {
            type: WorkingType.working,
            hours: [
                {
                    from: '08:00',
                    to: '12:00',
                },
                {
                    from: '13:00',
                    to: '17:00',
                },
            ],
        };
        const exceptions = [];
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i < currentYear + 10; i++) {
            //New Year’s Day — January 1
            exceptions.push({
                name: "New Year's Day",
                from: `20${i}-01-01`,
                to: `20${i}-01-01`,
                type: WorkingType.nonWorking,
            });
            //Juneteenth National Independence Day
            exceptions.push({
                name: 'Juneteenth',
                from: `20${i}-06-19`,
                to: `20${i}-06-19`,
                type: WorkingType.nonWorking,
            });
            //Independence Day — July 4
            exceptions.push({
                name: 'Independence Day',
                from: `20${i}-07-04`,
                to: `20${i}-07-04`,
                type: WorkingType.nonWorking,
            });
            //Veterans Day — November 11
            exceptions.push({
                name: 'Veterans Day',
                from: `20${i}-11-11`,
                to: `20${i}-11-11`,
                type: WorkingType.nonWorking,
            });
            //Christmas Day — December 25
            exceptions.push({
                name: 'Christmas Day',
                from: `20${i}-12-25`,
                to: `20${i}-12-25`,
                type: WorkingType.nonWorking,
            });
        }
        return {
            _id: 'default',
            unique_id: 1,
            name: 'Standard',
            type: 'GLOBAL',
            sunday: {
                type: WorkingType.nonWorking,
            },
            monday: { ...workingDay },
            tuesday: { ...workingDay },
            wednesday: { ...workingDay },
            thursday: { ...workingDay },
            friday: { ...workingDay },
            saturday: {
                type: WorkingType.nonWorking,
            },
            exceptions,
        };
    }
    static getTwentyFourHourCalendar() {
        const twentyFourHours = {
            type: WorkingType.working,
            hours: [{ from: '00:00', to: '24:00' }],
        };
        return new Calendar({
            _id: '24-hour',
            unique_id: this.TWENTY_FOUR_HOUR_UNIQUE_ID,
            name: '24 Hour',
            type: "GLOBAL",
            sunday: twentyFourHours,
            monday: twentyFourHours,
            tuesday: twentyFourHours,
            wednesday: twentyFourHours,
            thursday: twentyFourHours,
            friday: twentyFourHours,
            saturday: twentyFourHours,
            exceptions: [],
        });
    }
    _doc;
    _parent;
    _workingDateCache = new Map();
    constructor(doc) {
        this._doc = doc;
    }
    get uniqueId() {
        return this.doc.unique_id;
    }
    get parentUniqueId() {
        return this.doc.parent_unique_id;
    }
    get name() {
        return this.doc.name;
    }
    get doc() {
        return this._doc;
    }
    get days() {
        return [
            this.doc.sunday,
            this.doc.monday,
            this.doc.tuesday,
            this.doc.wednesday,
            this.doc.thursday,
            this.doc.friday,
            this.doc.saturday,
        ];
    }
    setParent(parent) {
        this._parent = parent;
    }
    get parent() {
        return this._parent;
    }
    secondsToWorkingDays(seconds) {
        return Math.round(seconds / (this.averageHoursPerDay() * DateUtils.SECONDS_PER_HOUR) * 10) / 10;
    }
    workingDaysToSeconds(days) {
        return Math.round(days * this.averageHoursPerDay() * DateUtils.SECONDS_PER_HOUR);
    }
    /**
     * Calculate duration in seconds between two dates, accounting for calendar working hours
     * Only counts working days and hours according to the calendar configuration
     * @param startDate - Start date
     * @param finishDate - Finish date
     * @returns Duration in seconds (working time only)
     */
    calculateDuration(startDate, finishDate) {
        if (!startDate || !finishDate) {
            return 0;
        }
        // Ensure we're working with Date objects
        const start = new Date(startDate);
        const finish = new Date(finishDate);
        // If finish is before start, return 0
        if (finish < start) {
            return 0;
        }
        let totalWorkingSeconds = 0;
        const currentDate = new Date(start);
        // Iterate day by day from start to finish
        while (currentDate <= finish) {
            const hoursForDay = this.getHoursForDate(currentDate);
            if (hoursForDay > 0) {
                // Check if this is the start or end day (partial day)
                if (this.isSameDay(currentDate, start) && this.isSameDay(currentDate, finish)) {
                    // Same day - calculate partial hours
                    const partialHours = this.calculatePartialDayHours(start, finish, currentDate, hoursForDay);
                    totalWorkingSeconds += partialHours * DateUtils.SECONDS_PER_HOUR;
                }
                else if (this.isSameDay(currentDate, start)) {
                    // Start day - calculate hours from start time to end of working day
                    const partialHours = this.calculatePartialDayHours(start, null, currentDate, hoursForDay);
                    totalWorkingSeconds += partialHours * DateUtils.SECONDS_PER_HOUR;
                }
                else if (this.isSameDay(currentDate, finish)) {
                    // End day - calculate hours from start of working day to finish time
                    const partialHours = this.calculatePartialDayHours(null, finish, currentDate, hoursForDay);
                    totalWorkingSeconds += partialHours * DateUtils.SECONDS_PER_HOUR;
                }
                else {
                    // Full working day
                    totalWorkingSeconds += hoursForDay * DateUtils.SECONDS_PER_HOUR;
                }
            }
            this.addUtcDays(currentDate, 1);
        }
        return Math.round(totalWorkingSeconds);
    }
    /**
     * Calculate partial day hours for start or end day
     * @param startTime - Start time (null if this is the end day)
     * @param endTime - End time (null if this is the start day)
     * @param date - The date to check
     * @param totalHoursForDay - Total working hours for this day
     * @returns Partial hours worked
     */
    calculatePartialDayHours(startTime, endTime, date, _totalHoursForDay) {
        const ranges = this.getTimeRangesForDate(date);
        if (ranges.length === 0) {
            return 0;
        }
        let partialHours = 0;
        const workingHours = ranges.map(hour => {
            const to = this.parseTimeString(hour.to);
            return {
                from: this.parseTimeString(hour.from),
                to: to === 0 ? 24 : to,
            };
        });
        if (startTime && endTime && this.isSameDay(startTime, endTime)) {
            // Same day - calculate hours between start and end within working hours
            const startHour = startTime.getUTCHours() + startTime.getUTCMinutes() / 60;
            const endHour = endTime.getUTCHours() + endTime.getUTCMinutes() / 60;
            workingHours.forEach(period => {
                const overlapStart = Math.max(startHour, period.from);
                const overlapEnd = Math.min(endHour, period.to);
                if (overlapEnd > overlapStart) {
                    partialHours += overlapEnd - overlapStart;
                }
            });
        }
        else if (startTime) {
            // Start day - calculate hours from start time to end of working day
            const startHour = startTime.getUTCHours() + startTime.getUTCMinutes() / 60;
            workingHours.forEach(period => {
                const overlapStart = Math.max(startHour, period.from);
                const overlapEnd = period.to;
                if (overlapEnd > overlapStart) {
                    partialHours += overlapEnd - overlapStart;
                }
            });
        }
        else if (endTime) {
            // End day - calculate hours from start of working day to end time
            const endHour = endTime.getUTCHours() + endTime.getUTCMinutes() / 60;
            workingHours.forEach(period => {
                const overlapStart = period.from;
                const overlapEnd = Math.min(endHour, period.to);
                if (overlapEnd > overlapStart) {
                    partialHours += overlapEnd - overlapStart;
                }
            });
        }
        return partialHours;
    }
    /**
     * Parse time string (HH:MM) to decimal hours
     * @param timeString - Time string in HH:MM format
     * @returns Decimal hours (e.g., "08:30" -> 8.5)
     */
    parseTimeString(timeString) {
        const parts = timeString.split(':');
        return Number.parseFloat(parts[0]) + Number.parseFloat(parts[1]) / 60;
    }
    getDate(date, duration) {
        if (duration == 0)
            return date;
        return duration < 0 ? this.getDateFromNegativeDuration(date, duration) : this.getDateFromPositiveDuration(date, duration);
    }
    /**
     * Subtract working duration from a finish date (MPXJ ProjectCalendar.getDateFromNegativeDuration).
     * A full day's remaining work ending at the finish time snaps to that day's start time,
     * not the finish clock time.
     */
    getDateFromNegativeDuration(finishDate, durationSeconds) {
        let remainingSeconds = Math.round(Math.abs(durationSeconds));
        if (remainingSeconds === 0) {
            return new Date(finishDate);
        }
        let currentDayEnd = new Date(finishDate);
        let currentDayStart = new Date(finishDate);
        if (this.isMidnight(currentDayEnd)) {
            this.addUtcDays(currentDayStart, -1);
        }
        currentDayStart.setUTCHours(0, 0, 0, 0);
        let dayIterations = 0;
        while (remainingSeconds > 0) {
            if (++dayIterations > Calendar.MAX_NONWORKING_DAYS) {
                break;
            }
            const currentDayWorkingSeconds = Math.round(this.getWork(currentDayStart, currentDayEnd) ?? 0);
            if (remainingSeconds === currentDayWorkingSeconds) {
                currentDayStart = this.setDateHours(currentDayStart, this.getStartTime(currentDayStart) ?? 8);
                break;
            }
            if (remainingSeconds > currentDayWorkingSeconds) {
                remainingSeconds -= currentDayWorkingSeconds;
                let currentDay = new Date(currentDayStart);
                let nonWorkingDayCount = 0;
                do {
                    this.addUtcDays(currentDay, -1);
                    ++nonWorkingDayCount;
                    if (nonWorkingDayCount > Calendar.MAX_NONWORKING_DAYS) {
                        currentDay = new Date(currentDayStart);
                        this.addUtcDays(currentDay, -1);
                        remainingSeconds = 0;
                        break;
                    }
                } while (!this.isWorkingDate(currentDay));
                currentDayStart = currentDay;
                currentDayEnd = this.setDateHours(currentDayStart, this.getFinishTime(currentDayStart) ?? 17);
            }
            else {
                const ranges = [...this.getRanges(currentDayStart)].reverse();
                const currentDayEndTime = this.getDecimalHours(currentDayEnd);
                let lastRange = true;
                for (const range of ranges) {
                    let rangeStart = this.parseTimeString(range.from);
                    let rangeEnd = this.parseTimeString(range.to);
                    if (rangeEnd === 0) {
                        rangeEnd = 24;
                    }
                    if (!this.isMidnightHours(currentDayEndTime) && currentDayEndTime < rangeStart) {
                        continue;
                    }
                    if (lastRange && (this.isMidnightHours(rangeEnd) || rangeEnd > currentDayEndTime)) {
                        rangeEnd = currentDayEndTime === 0 ? 24 : currentDayEndTime;
                    }
                    lastRange = false;
                    const rangeSeconds = Math.round((rangeEnd - rangeStart) * DateUtils.SECONDS_PER_HOUR);
                    if (rangeSeconds <= 0) {
                        continue;
                    }
                    if (remainingSeconds > rangeSeconds) {
                        remainingSeconds -= rangeSeconds;
                    }
                    else {
                        if (remainingSeconds !== rangeSeconds) {
                            rangeStart = rangeEnd - remainingSeconds / DateUtils.SECONDS_PER_HOUR;
                        }
                        currentDayStart = this.setDateHours(currentDayStart, rangeStart);
                        remainingSeconds = 0;
                        break;
                    }
                }
                if (remainingSeconds > 0) {
                    break;
                }
            }
        }
        return currentDayStart;
    }
    isMidnight(date) {
        return date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0;
    }
    isMidnightHours(hours) {
        return hours === 0 || hours === 24;
    }
    getDecimalHours(date) {
        return date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    }
    getDateFromPositiveDuration(startDate, durationSeconds) {
        const MAX_NONWORKING_DAYS = 1000;
        let remainingHours = durationSeconds / DateUtils.SECONDS_PER_HOUR;
        if (remainingHours == 0) {
            return startDate;
        }
        let currentDay = new Date(startDate);
        // If the start date is on a non-working day, adjust to the next working day
        if (!this.isWorkingDate(currentDay)) {
            let nonWorkingDayCount = 0;
            do {
                this.addUtcDays(currentDay, 1);
                ++nonWorkingDayCount;
                if (nonWorkingDayCount > MAX_NONWORKING_DAYS) {
                    this.addUtcDays(currentDay, 1);
                    remainingHours = 0;
                    break;
                }
            } while (!this.isWorkingDate(currentDay));
            // Set the time to the start of the working day
            const startTime = this.getStartTime(currentDay);
            if (startTime !== null) {
                currentDay = this.setDateHours(currentDay, startTime);
            }
        }
        let currentDayEnd = new Date(currentDay);
        currentDayEnd.setUTCHours(0, 0, 0, 0);
        currentDayEnd.setUTCDate(currentDayEnd.getUTCDate() + 1);
        let dayIterations = 0;
        while (remainingHours > 0) {
            if (++dayIterations > Calendar.MAX_NONWORKING_DAYS) {
                break;
            }
            const currentDateWorkingSeconds = this.getWork(currentDay, currentDayEnd) ?? 0;
            const currentDateWorkingHours = currentDateWorkingSeconds / DateUtils.SECONDS_PER_HOUR;
            if (remainingHours == currentDateWorkingHours) {
                return this.setDateHours(currentDay, this.getFinishTime(currentDay) ?? 17);
            }
            if (remainingHours > currentDateWorkingHours) {
                if (currentDateWorkingHours > 0) {
                    remainingHours -= currentDateWorkingHours;
                }
                //
                // Move the calendar forward to the next working day
                //
                //let currentDay = currentDayStart;
                let nonWorkingDayCount = 0;
                do {
                    this.addUtcDays(currentDay, 1);
                    ++nonWorkingDayCount;
                    if (nonWorkingDayCount > MAX_NONWORKING_DAYS) {
                        this.addUtcDays(currentDay, 1);
                        remainingHours = 0;
                        break;
                    }
                } while (!this.isWorkingDate(currentDay));
                // Set the time to the start of the new working day
                const startTime = this.getStartTime(currentDay);
                if (startTime !== null) {
                    currentDay = this.setDateHours(currentDay, startTime);
                }
                // Update currentDayEnd to point to the day after the new currentDay
                currentDayEnd = new Date(currentDay);
                currentDayEnd.setUTCHours(0, 0, 0, 0);
                currentDayEnd.setUTCDate(currentDayEnd.getUTCDate() + 1);
                // Check if we have exactly the right number of hours for this new day
                const newDayWorkingHours = this.getHoursForDate(currentDay);
                if (remainingHours == newDayWorkingHours) {
                    return this.setDateHours(currentDay, this.getFinishTime(currentDay) ?? 17);
                }
            }
            else {
                //
                // We have fewer hours to allocate than there are working hours
                // in this day. We need to calculate the time of day at which
                // our work ends.
                //
                const ranges = this.getTimeRangesForDate(currentDay);
                const currentDayStartTime = currentDay.getUTCHours() + currentDay.getUTCMinutes() / 60;
                let firstRange = true;
                let foundUsableRange = false;
                for (const range of ranges) {
                    //
                    // Skip this range if its end is before our start time
                    //
                    let rangeStart = this.parseTimeString(range.from);
                    let rangeEnd = this.parseTimeString(range.to);
                    if (rangeStart == null || rangeEnd == null) {
                        continue;
                    }
                    if (firstRange && rangeEnd != 24 && rangeEnd < currentDayStartTime) {
                        continue;
                    }
                    //
                    // Move the start of the range if our current start is
                    // past the range start
                    //
                    if (firstRange && rangeStart < currentDayStartTime) {
                        rangeStart = currentDayStartTime;
                    }
                    // If the start time is past the end of this range, skip it
                    if (rangeStart >= rangeEnd) {
                        continue;
                    }
                    firstRange = false;
                    foundUsableRange = true;
                    // Calculate available hours in this range (after adjusting for start time)
                    const availableHoursInRange = rangeEnd - rangeStart;
                    if (remainingHours > availableHoursInRange) {
                        // Use all available hours in this range and move to next range
                        remainingHours -= availableHoursInRange;
                    }
                    else {
                        // Calculate the end time within this range
                        rangeEnd = rangeStart + remainingHours;
                        currentDayEnd = this.setDateHours(currentDay, rangeEnd);
                        remainingHours = 0;
                        break;
                    }
                }
                // If we didn't find any usable range (start time is past all working hours),
                // move to the next working day
                if (!foundUsableRange && remainingHours > 0) {
                    let nonWorkingDayCount = 0;
                    do {
                        this.addUtcDays(currentDay, 1);
                        ++nonWorkingDayCount;
                        if (nonWorkingDayCount > MAX_NONWORKING_DAYS) {
                            this.addUtcDays(currentDay, 1);
                            remainingHours = 0;
                            break;
                        }
                    } while (!this.isWorkingDate(currentDay));
                    // Set the time to the start of the new working day
                    const startTime = this.getStartTime(currentDay);
                    if (startTime !== null) {
                        currentDay = this.setDateHours(currentDay, startTime);
                    }
                    // Update currentDayEnd to point to the day after the new currentDay
                    currentDayEnd = new Date(currentDay);
                    currentDayEnd.setUTCHours(0, 0, 0, 0);
                    this.addUtcDays(currentDayEnd, 1);
                }
            }
        }
        return currentDayEnd;
    }
    getHoursForDate(date) {
        const timeRanges = this.getTimeRangesForDate(date);
        return timeRanges.reduce((acc, timeRange) => acc + this.timeRangeToHours(timeRange), 0);
    }
    /**
     * Get working hours for a specific date, considering calendar hierarchy
     * Checks current calendar first, then parent, then grandparent, etc.
     * @param date - The date to check
     * @param dateString - ISO format date string (YYYY-MM-DD)
     * @returns Number of working hours for this date
     */
    getTimeRangesForDate(date) {
        // Check for exceptions first (exceptions override everything)
        const exception = this.getExceptionForDate(date.toISOString().split('T')[0]);
        if (exception) {
            if (exception.type === WorkingType.nonWorking) {
                return [];
            }
            if (exception.type === WorkingType.working) {
                // For working exceptions, calculate hours from the exception or use default
                // For now, use the calendar's default hours
                return this.getHoursFromDayConfig(date, true);
            }
        }
        // No exception, check regular calendar day (with parent fallback)
        return this.getHoursFromDayConfig(date, false);
    }
    /**
     * Get hours from day configuration, with parent calendar fallback
     * @param date - The date to check
     * @param forceWorking - If true, assume the day is working and just get hours
     * @returns Number of working hours
     */
    getHoursFromDayConfig(date, forceWorking) {
        const dayOfWeek = date.getUTCDay();
        const dayConfig = this.days[dayOfWeek];
        // Check if this calendar defines the day
        if (dayConfig) {
            if (forceWorking || dayConfig.type === WorkingType.working) {
                if (dayConfig.hours && dayConfig.hours.length > 0) {
                    return dayConfig.hours;
                }
            }
            else if (dayConfig.type === WorkingType.nonWorking) {
                // Explicitly marked as non-working in this calendar
                return [];
            }
        }
        // If this calendar doesn't define working hours for this day, check parent
        if (this.parent) {
            return this.parent.getHoursFromDayConfig(date, forceWorking);
        }
        // No parent and no hours defined - return 0
        return [];
    }
    /**
     * Check if a date falls within a calendar exception period
     * Also checks parent calendars if no exception found in current calendar
     * @param dateString - Date string in ISO format (YYYY-MM-DD)
     * @returns The exception if found, null otherwise
     */
    getExceptionForDate(dateString) {
        // Check current calendar's exceptions first
        if (this.doc.exceptions && this.doc.exceptions.length > 0) {
            for (const exception of this.doc.exceptions) {
                if (dateString >= exception.from && dateString <= exception.to) {
                    return exception;
                }
            }
        }
        // If no exception found and there's a parent, check parent's exceptions
        if (this.parent) {
            return this.parent.getExceptionForDate(dateString);
        }
        return null;
    }
    averageHoursPerDay() {
        return this.getHoursPerWeek() / this.getWorkingDaysPerWeek() || 8;
    }
    averageSecondsPerDay() {
        return this.averageHoursPerDay() * DateUtils.SECONDS_PER_HOUR;
    }
    workingDaysPerWeek() {
        return this.days.filter(dayData => dayData?.type === WorkingType.working && (dayData.hours?.length ?? 0) > 0).length;
    }
    getHoursPerWeek() {
        let totalHours = 0;
        this.days.forEach(dayData => {
            if (dayData?.type === WorkingType.working && dayData.hours && dayData.hours.length > 0) {
                dayData.hours.forEach(hour => {
                    totalHours += this.timeRangeToHours(hour);
                });
            }
        });
        return totalHours;
    }
    timeRangeToHours(range) {
        const fromParts = range.from.split(':');
        const toParts = range.to.split(':');
        const fromHours = Number.parseFloat(fromParts[0]) + Number.parseFloat(fromParts[1]) / 60;
        const toHours = Number.parseFloat(toParts[0]) + Number.parseFloat(toParts[1]) / 60;
        return toHours - fromHours;
    }
    /**
   * Calculate number of working days per week for a calendar
   * @param calendarDoc - The calendar document
   * @returns Number of working days in the week (0-7)
   */
    getWorkingDaysPerWeek() {
        return this.days.filter(dayData => dayData?.type === WorkingType.working && (dayData.hours?.length ?? 0) > 0).length;
    }
    getWorkEndOfDay(date) {
        const endDate = new Date(date);
        endDate.setUTCHours(23, 59, 59, 999);
        return this.getPreviousWorkFinish(endDate);
    }
    getWorkStartOfDay(date) {
        const startDate = new Date(date);
        startDate.setUTCHours(0, 0, 0, 0);
        return this.getNextWorkStart(startDate);
    }
    /**
     * Utility method to retrieve the next working date start time, given
     * a date and time as a starting point.
     *
     * @param date date and time start point
     * @return date and time of next work start
     */
    getNextWorkStart(date) {
        const originalDate = new Date(date);
        if (this.isWorkingDate(originalDate)) {
            const ranges = this.getTimeRangesForDate(originalDate);
            const calTime = originalDate.getUTCHours() + originalDate.getUTCMinutes() / 60 + originalDate.getUTCSeconds() / 3600;
            let startTime = null;
            for (const range of ranges) {
                const rangeStart = this.parseTimeString(range.from);
                let rangeEnd = this.parseTimeString(range.to);
                if (rangeEnd === 0) {
                    rangeEnd = 24;
                }
                if (rangeEnd === 24 || calTime < rangeEnd) {
                    startTime = calTime > rangeStart ? calTime : rangeStart;
                    break;
                }
            }
            if (startTime !== null) {
                return this.setDateHours(new Date(originalDate), startTime);
            }
        }
        let nonWorkingDayCount = 0;
        let nextDate = new Date(originalDate);
        do {
            nextDate.setUTCDate(nextDate.getUTCDate() + 1);
            nonWorkingDayCount++;
            if (nonWorkingDayCount > Calendar.MAX_NONWORKING_DAYS) {
                nextDate = new Date(originalDate);
                break;
            }
        } while (!this.isWorkingDate(nextDate));
        return this.setDateHours(nextDate, this.getStartTime(nextDate) ?? 8);
    }
    /**
     * Check if a date is a working date (has working hours)
     * @param date - The date to check
     * @returns true if the date is a working date
     */
    isWorkingDate(date) {
        return this.getHoursForDate(date) > 0;
    }
    /**
     * This method retrieves the amount of
     * work in seconds between two dates based on this calendar.
     *
     * @param startDate start date
     * @param endDate end date
     * @return amount of work in seconds
     */
    getWork(startDate, endDate) {
        if (startDate == null || endDate == null) {
            return null;
        }
        // Create cache key from date range
        const cacheKey = `${startDate.getTime()}-${endDate.getTime()}`;
        const cachedResult = this._workingDateCache.get(cacheKey);
        if (cachedResult !== undefined) {
            return cachedResult;
        }
        // We want the start date to be the earliest date, and the end date
        // to be the latest date. Set a flag here to indicate if we have swapped
        // the order of the supplied dates.
        let invert = false;
        let actualStart = new Date(startDate);
        let actualEnd = new Date(endDate);
        if (actualStart > actualEnd) {
            invert = true;
            const temp = actualStart;
            actualStart = actualEnd;
            actualEnd = temp;
        }
        let totalTime = 0; // in seconds
        if (this.isSameDay(actualStart, actualEnd)) {
            // Same day - calculate partial hours
            const hoursForDay = this.getHoursForDate(actualStart);
            if (hoursForDay > 0) {
                const partialHours = this.calculatePartialDayHours(actualStart, actualEnd, actualStart, hoursForDay);
                totalTime = partialHours * DateUtils.SECONDS_PER_HOUR;
            }
        }
        else {
            // Different days - process first day, middle days, and last day
            const canonicalEndDate = new Date(actualEnd);
            canonicalEndDate.setUTCHours(0, 0, 0, 0);
            // Find the first working day in the range
            let currentDate = new Date(actualStart);
            while (!this.isWorkingDate(currentDate) && currentDate < canonicalEndDate) {
                this.addUtcDays(currentDate, 1);
                currentDate.setUTCHours(0, 0, 0, 0);
            }
            if (currentDate < canonicalEndDate) {
                // If the first working day is the same as the start date, we leave
                // the date alone to preserve the start time. If we have moved past
                // the start date to find the first working day, reset the time
                // of day to ensure that we use all working hours on this day.
                const isStartDate = this.isSameDay(currentDate, actualStart);
                const targetDate = isStartDate ? new Date(actualStart) : new Date(currentDate);
                // Calculate the amount of working time for this day
                const hoursForDay = this.getHoursForDate(currentDate);
                if (hoursForDay > 0) {
                    // For first day, calculate from target time to end of working day
                    const partialHours = this.calculatePartialDayHours(targetDate, null, currentDate, hoursForDay);
                    totalTime += partialHours * DateUtils.SECONDS_PER_HOUR;
                }
                // Process each working day until we reach the last day
                let nextDate = new Date(currentDate);
                let spannedDays = 0;
                while (true) {
                    this.addUtcDays(nextDate, 1);
                    nextDate.setUTCHours(0, 0, 0, 0);
                    spannedDays++;
                    if (nextDate >= canonicalEndDate || spannedDays > Calendar.MAX_NONWORKING_DAYS) {
                        break;
                    }
                    // Skip this day if it has no working time
                    const hoursForNextDay = this.getHoursForDate(nextDate);
                    if (hoursForNextDay === 0) {
                        continue;
                    }
                    // Add the working time for the whole day
                    totalTime += hoursForNextDay * DateUtils.SECONDS_PER_HOUR;
                }
            }
            // We are now at the last day
            const hoursForEndDay = this.getHoursForDate(actualEnd);
            if (hoursForEndDay > 0) {
                // Calculate from start of working day to end time
                const partialHours = this.calculatePartialDayHours(null, actualEnd, actualEnd, hoursForEndDay);
                totalTime += partialHours * DateUtils.SECONDS_PER_HOUR;
            }
        }
        if (invert) {
            totalTime = -totalTime;
        }
        // Cache the result
        this._workingDateCache.set(cacheKey, totalTime);
        return totalTime;
    }
    /**
     * Check if two dates are on the same day
     * @param date1 first date
     * @param date2 second date
     * @returns true if both dates are on the same day
     */
    isSameDay(date1, date2) {
        return (date1.getUTCFullYear() === date2.getUTCFullYear() &&
            date1.getUTCMonth() === date2.getUTCMonth() &&
            date1.getUTCDate() === date2.getUTCDate());
    }
    /** Advance a date by whole UTC days. Local `setDate` can stall across DST. */
    addUtcDays(date, days) {
        date.setUTCDate(date.getUTCDate() + days);
        return date;
    }
    getWorkFinish(date) {
        if (this.isWorkingDate(date)) {
            const finishTime = this.getFinishTime(date);
            if (finishTime != null) {
                return this.setDateHours(date, finishTime);
            }
            // Fallback: return date at default finish time (5 PM)
            return this.setDateHours(date, 17);
        }
        // It's a non-working day - find the previous working day and return it at finish time
        let nonWorkingDayCount = 0;
        let previousDate = new Date(date);
        do {
            previousDate.setUTCDate(previousDate.getUTCDate() - 1);
            ++nonWorkingDayCount;
            if (nonWorkingDayCount > Calendar.MAX_NONWORKING_DAYS) {
                // Can't find previous working day, return original date unchanged
                return date;
            }
        } while (!this.isWorkingDate(previousDate));
        const finishTime = this.getFinishTime(previousDate);
        if (finishTime != null) {
            return this.setDateHours(previousDate, finishTime);
        }
        // Fallback: return previous date at default finish time (5 PM)
        return this.setDateHours(previousDate, 17);
    }
    /**
     * Utility method to retrieve the previous working date finish time, given
     * a date and time as a starting point.
     *
     * @param date date and time start point
     * @return date and time of previous work finish
     */
    getPreviousWorkFinish(date) {
        const originalDate = new Date(date);
        if (!this.isWorkingDate(originalDate)) {
            let nonWorkingDayCount = 0;
            let previousDate = new Date(originalDate);
            do {
                previousDate.setUTCDate(previousDate.getUTCDate() - 1);
                ++nonWorkingDayCount;
                if (nonWorkingDayCount > Calendar.MAX_NONWORKING_DAYS) {
                    return originalDate;
                }
            } while (!this.isWorkingDate(previousDate));
            const finishTime = this.getFinishTime(previousDate);
            if (finishTime != null) {
                return this.setDateHours(previousDate, finishTime);
            }
            return this.setDateHours(previousDate, 17);
        }
        const ranges = this.getRanges(originalDate);
        if (ranges != null && ranges.length > 0) {
            let finishTime = null;
            const calTime = originalDate.getUTCHours() + originalDate.getUTCMinutes() / 60 + originalDate.getUTCSeconds() / 3600;
            let dayForResult = new Date(originalDate);
            for (let index = ranges.length - 1; index >= 0; index--) {
                const range = ranges[index];
                const toTime = this.parseTimeString(range.to);
                const rangeEnd = range.to === "24:00" ? 24 : toTime;
                if ((range.to === "24:00" && calTime === 24) || calTime >= rangeEnd) {
                    finishTime = toTime;
                    break;
                }
            }
            if (finishTime == null) {
                let nonWorkingDayCount = 0;
                do {
                    dayForResult.setUTCDate(dayForResult.getUTCDate() - 1);
                    ++nonWorkingDayCount;
                    if (nonWorkingDayCount > Calendar.MAX_NONWORKING_DAYS) {
                        dayForResult = new Date(originalDate);
                        break;
                    }
                } while (!this.isWorkingDate(dayForResult));
                finishTime = this.getFinishTime(dayForResult);
                if (finishTime == null) {
                    finishTime = 17;
                }
            }
            return this.setDateHours(dayForResult, finishTime);
        }
        return originalDate;
    }
    /**
  * Retrieves the time at which work finishes on the given date, or returns
  * null if this is a non-working day.
  *
  * @param date Date instance
  * @return finish time, or null for non-working day
  */
    getFinishTime(date) {
        if (date == null) {
            return null;
        }
        const ranges = this.getRanges(date);
        if (ranges == null || ranges.length === 0) {
            return null;
        }
        return this.parseTimeString(ranges[ranges.length - 1].to);
    }
    getStartTime(date) {
        const ranges = this.getRanges(date);
        if (ranges == null || ranges.length === 0) {
            return null;
        }
        return this.parseTimeString(ranges[0].from);
    }
    getDayConfig(date) {
        const dayOfWeek = date.getUTCDay();
        return this.days[dayOfWeek];
    }
    getRanges(date) {
        return this.getTimeRangesForDate(date);
    }
    setDateHours(date, h) {
        const hours = Math.floor(h);
        const minutes = Math.floor((h - hours) * 60);
        const seconds = Math.floor(((h - hours) * 60 - minutes) * 60);
        const newDate = new Date(date);
        newDate.setUTCHours(hours, minutes, seconds, 0);
        return newDate;
    }
    /**
     * Count working days in the inclusive range [startDate, endDate].
     * Returns 0 if end is before start or either date is missing.
     */
    workDaysBetween(startDate, endDate) {
        if (!startDate || !endDate || endDate < startDate) {
            return 0;
        }
        let days = 0;
        const currentDate = new Date(startDate);
        currentDate.setUTCHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setUTCHours(0, 0, 0, 0);
        while (currentDate <= end) {
            if (this.isWorkingDate(currentDate)) {
                days++;
            }
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }
        return days;
    }
}
